import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineDotsVertical, HiOutlineTrash, HiOutlineUserGroup, HiCheck, HiX, HiOutlineExclamationCircle, HiDownload, HiHome } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import useOrganizationService from '../../../services/organization';
import useResourcesService from '../../../services/resources';
import { useJwt } from '../../../helpers/common';
import { SearchMode } from '../../Resource/types';
import CustomButton from "../../../components/common/CustomButton";
import CustomTooltip from "../../../components/common/CustomTooltip";
import TruncatedText from '../../../components/common/TruncatedText';

interface TeamMember {
  id: number;
  name: string;
  email: string;
  image_url: string;
}

interface Project {
  project_id: string;
  project_name: string;
}
interface Team {
  id: number;
  name: string;
  members: TeamMember[];
  is_deactivated: boolean;
  projects?: Project[];
  imported?: boolean;
  story_points?: number | null;
}

interface TeamsContentProps {
  searchTerm?: string;
  searchMode?: SearchMode;
  onEditTeam?: (team: Team) => void;
  showOnlyTeamsWithChanges?: boolean;
  onToggleTeamsWithChanges?: (show: boolean) => void;
}

// Utility function extracted for better performance
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Optimized search filter function using efficient string matching
const filterTeams = (teams: Team[], searchTerm: string, searchMode: SearchMode): Team[] => {
  if (!searchTerm?.trim()) return teams;

  const searchLower = searchTerm.toLowerCase();

  return teams.filter((team: Team) => {
    if (searchMode === SearchMode.NAME) {
      return team.name.toLowerCase().includes(searchLower);
    } else if (searchMode === SearchMode.SKILLS) {
      // Use some() for early termination - more efficient than filter + length check
      return team.members.some((member: TeamMember) =>
        member.name.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });
};

const SHOW_ONLY_ACTIVE_STORAGE_KEY = 'cadence_teams_show_only_active';
const HIDE_IMPORTED_TEAMS_STORAGE_KEY = 'cadence_teams_hide_imported';

const TeamsContent: React.FC<TeamsContentProps> = memo(({
  searchTerm = '',
  searchMode = SearchMode.NAME,
  onEditTeam,
  showOnlyTeamsWithChanges = false,
  onToggleTeamsWithChanges
}) => {
  const { deleteTeam, fetchTeams, activateTeam, deactivateTeam } = useOrganizationService();
  const { fetchManagerResources } = useResourcesService();
  const { teams, teamsError, teamsFetched, orgDetails } = useSelector((state: any) => state.organization);
  const { syncDifferences } = useSelector((state: any) => state.azureTeams);
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const navigate = useNavigate();
  const justClosedDropdown = useRef(false);

  // Show only active teams toggle state (separate from teams with changes filter)
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(false);
  const [hideImported, setHideImported] = useState<boolean>(false);

  // Initialize showOnlyActive state from localStorage
  useEffect(() => {
    const storedValue = localStorage.getItem(SHOW_ONLY_ACTIVE_STORAGE_KEY);
    if (storedValue !== null) {
      setShowOnlyActive(storedValue === 'true');
    }
    const storedValueImported = localStorage.getItem(HIDE_IMPORTED_TEAMS_STORAGE_KEY);
    if (storedValueImported !== null) {
      setHideImported(storedValueImported === 'true');
    }
  }, []);

  // Toggle handler with localStorage persistence
  const handleToggleShowOnlyActive = useCallback(() => {
    setShowOnlyActive(prevState => {
      const newState = !prevState;
      localStorage.setItem(SHOW_ONLY_ACTIVE_STORAGE_KEY, String(newState));
      return newState;
    });
  }, []);

  const handleToggleHideImported = useCallback(() => {
    setHideImported(prevState => {
      const newState = !prevState;
      localStorage.setItem(HIDE_IMPORTED_TEAMS_STORAGE_KEY, String(newState));
      return newState;
    });
  }, []);

  // Optimized state management - using single state object for related data
  const [uiState, setUiState] = useState({
    dropdownOpenTeam: null as number | null,
    showDeleteConfirm: false,
    teamToDelete: null as { id: number; name: string } | null
  });

  // Memoized filtered teams for optimal performance with sorting active first and optional filtering
  const filteredTeams = useMemo(() => {
    let filtered = teams ? filterTeams(teams, searchTerm, searchMode) : [];

    // Apply teams with changes filter if enabled
    if (showOnlyTeamsWithChanges && syncDifferences?.length > 0) {
      const teamsWithChangesIds = new Set(syncDifferences.map((diff: any) => diff.id));
      filtered = filtered.filter(team => teamsWithChangesIds.has(team.id));
    }

    // Apply active-only filter if enabled (only when not showing teams with changes)
    if (!showOnlyTeamsWithChanges && showOnlyActive) {
      filtered = filtered.filter(team => !team.is_deactivated);
    }

    // Apply imported filter
    if (hideImported) {
      filtered = filtered.filter(team => !team.imported);
    }

    // Sort teams - active teams first, then inactive teams
    return [...filtered].sort((a, b) => {
      if (a.is_deactivated === b.is_deactivated) {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        const nameCompare = nameA.localeCompare(nameB);
        if (nameCompare !== 0) return nameCompare;
        const projectA = (a.projects && a.projects[0]?.project_name) ? a.projects[0].project_name.toLowerCase() : '';
        const projectB = (b.projects && b.projects[0]?.project_name) ? b.projects[0].project_name.toLowerCase() : '';
        return projectA.localeCompare(projectB);
      }
      return a.is_deactivated ? 1 : -1; // Active teams come first
    });
  }, [teams, searchTerm, searchMode, showOnlyTeamsWithChanges, syncDifferences, showOnlyActive, hideImported]);

  const handleCardClick = useCallback((team: Team, e: React.MouseEvent) => {
    if (uiState.dropdownOpenTeam !== null) {
      e.stopPropagation();
      e.preventDefault();
      setUiState(prev => ({ ...prev, dropdownOpenTeam: null }));
      return;
    }
    if (justClosedDropdown.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    onEditTeam?.(team);
  }, [uiState.dropdownOpenTeam, onEditTeam]);

  // Optimized dropdown close handler with useCallback
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (target?.closest('.team-dropdown') || target?.closest('.delete-team-button')) {
      return;
    }
    if (uiState.dropdownOpenTeam !== null) {
      setUiState(prev => ({ ...prev, dropdownOpenTeam: null }));
      justClosedDropdown.current = true;
      setTimeout(() => {
        justClosedDropdown.current = false;
      }, 100);
    }
  }, [uiState.dropdownOpenTeam]);

  // Optimized event handlers with useCallback to prevent unnecessary re-renders
  const handleDeleteClick = useCallback((teamId: number, teamName: string) => {
    setUiState({
      dropdownOpenTeam: null,
      showDeleteConfirm: true,
      teamToDelete: { id: teamId, name: teamName }
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!uiState.teamToDelete || !org_id) {
      return;
    }

    try {
      await deleteTeam(org_id, String(uiState.teamToDelete.id));
      setUiState({
        dropdownOpenTeam: null,
        showDeleteConfirm: false,
        teamToDelete: null
      });
    } catch (error) {
      // Error handled in service
    }
  }, [uiState.teamToDelete, org_id, deleteTeam]);

  const handleCancelDelete = useCallback(() => {
    setUiState(prev => ({
      ...prev,
      showDeleteConfirm: false,
      teamToDelete: null
    }));
  }, []);

  const handleDropdownToggle = useCallback((teamId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setUiState(prev => ({
      ...prev,
      dropdownOpenTeam: prev.dropdownOpenTeam === teamId ? null : teamId
    }));
  }, []);

  // Toggle team active status using actual API
  const handleToggleActive = useCallback(async (team: Team, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (org_id) {
      try {
        if (team.is_deactivated) {
          // If team is currently deactivated, activate it
          await activateTeam(org_id, String(team.id));
        } else {
          // If team is currently active, deactivate it
          await deactivateTeam(org_id, String(team.id));
        }
        
        // Refresh users/people after team status change
        await fetchManagerResources(org_id);
      } catch (error) {
        console.error('Error toggling team status:', error);
      }
    }

    // Close dropdown after toggling status
    setUiState(prev => ({ ...prev, dropdownOpenTeam: null }));
    justClosedDropdown.current = true;
    setTimeout(() => {
      justClosedDropdown.current = false;
    }, 100);
  }, [org_id, activateTeam, deactivateTeam, fetchManagerResources]);

  // Prevent clicks on inactive team cards from propagating to parent
  const handleInactiveCardClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Optimized click outside effect
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Auto-refresh teams when teamsFetched flag is reset (e.g., after team operations)
  useEffect(() => {
    if (org_id && !teamsFetched) {
      fetchTeams(org_id);
    }
  }, [org_id, teamsFetched, fetchTeams]);

  // Azure sync differences are now fetched at root level

  // Helper function to check if team has sync differences
  const getTeamSyncDifference = useCallback((teamId: number) => {
    return syncDifferences?.find((diff: any) => diff.id === teamId);
  }, [syncDifferences]);

  // Early returns for error and empty states - more efficient than conditional rendering
  if (teamsError) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-2xl mb-6">
          <HiOutlineUserGroup className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-light-text-primary dark:text-light-text-inverse mb-2">
          Unable to Load Teams
        </h3>
        <p className="text-light-text-secondary dark:text-light-text-muted">
          Something went wrong. Please try refreshing the page.
        </p>
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl mb-6">
          <HiOutlineUserGroup className="w-10 h-10 text-blue-500" />
        </div>
        <h3 className="text-xl font-bold text-light-text-primary dark:text-light-text-inverse mb-2">
          No Teams Yet
        </h3>
        <p className="text-light-text-secondary dark:text-light-text-muted max-w-md mx-auto">
          Your organization hasn't created any teams yet. Teams will appear here once they're set up.
        </p>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-hide {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-show:hover {
            scrollbar-width: thin;
          }
          .scrollbar-show:hover::-webkit-scrollbar {
            display: block;
            width: 4px;
          }
          .scrollbar-show:hover::-webkit-scrollbar-track {
            background: transparent;
          }
          .scrollbar-show:hover::-webkit-scrollbar-thumb {
            background-color: rgb(156 163 175);
            border-radius: 2px;
          }
          .scrollbar-show:hover::-webkit-scrollbar-thumb:hover {
            background-color: rgb(107 114 128);
          }
          .team-card-content {
            opacity: 1;
          }
          .inactive-team .team-card-content {
            opacity: 0.6;
            filter: saturate(0.5);
          }
          .inactive-team .team-actions {
            opacity: 1;
            filter: saturate(1);
          }
          
          /* Toggle switch styles */
          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 48px;
            height: 24px;
            margin-left: 8px;
            margin-right: 8px;
          }
          
          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          
          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .4s;
            border-radius: 24px;
          }
          
          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 16px;
            width: 16px;
            left: 4px;
            bottom: 4px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
          }
          
          input:checked + .toggle-slider {
            background-color: #10B981;
          }
          
          input:focus + .toggle-slider {
            box-shadow: 0 0 1px #10B981;
          }
          
          input:checked + .toggle-slider:before {
            transform: translateX(24px);
          }
          
          .dark input:checked + .toggle-slider {
            background-color: #059669;
          }
        `
      }} />

      <div className="min-h-screen  rounded-2xl dark:bg-secondary-dark">
        {/* Teams Grid */}
        <div className="px-6 py-6">
          {/* Teams Header - Similar to People page */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                All Teams ({filteredTeams.length})
              </h3>
            </div>

            <div className="flex items-center ml-4 gap-6">
              {showOnlyTeamsWithChanges ? (
                <CustomButton
                  onClick={() => onToggleTeamsWithChanges?.(false)}
                  className="whitespace-nowrap bg-light-surfacePrimary hover:bg-light-surfaceHover dark:bg-secondary-dark
                    dark:hover:bg-tertiary-dark border dark:border-slate-700  border-light-border-default hover:border-light-border-interactive
                      !px-3 !py-1.5 shadow-sm hover:shadow-md transition-all duration-200 !text-black dark:!text-white"
                  isLoading={false}
                  disabled={false}
                >
                  Show All Teams
                </CustomButton>
              ) : (
                <>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      checked={showOnlyActive}
                      onChange={handleToggleShowOnlyActive}
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-100">Exclude deactivated teams</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      checked={hideImported}
                      onChange={handleToggleHideImported}
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-100">Exclude imported teams</span>
                  </label>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-8">
            {filteredTeams
              .map((team: Team) => {
                const teamSyncDiff = getTeamSyncDifference(team.id);
                const hasChanges = teamSyncDiff && teamSyncDiff.members && teamSyncDiff.members.length > 0;

                return (
                  <CustomTooltip
                    title={team.is_deactivated ? "This team is deactivated and won't be suggested for new work items but previous assignments remain visible." : ""}
                    placement="top"
                  >
                    <div
                      key={team.id}
                      className="relative h-full"
                    >
                      <div
                        onClick={!team.is_deactivated ? ((e) => handleCardClick(team, e)) : handleInactiveCardClick}
                        className={`group h-full bg-white dark:bg-gray-800 rounded-xl border transition-all duration-300 
                         ${!team.is_deactivated ? 'border-gray-200 dark:border-gray-700' : 'border-gray-200 dark:border-gray-700 inactive-team'}
                         ${!team.is_deactivated ? 'hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/40 hover:shadow-xl dark:hover:shadow-2xl' : 'cursor-not-allowed'}
                         ${!team.is_deactivated ? 'cursor-pointer' : ''} overflow-hidden`}
                      >
                        {/*${hasChanges && !showOnlyTeamsWithChanges ? 'inset-ring ring-yellow-400 dark:ring-yellow-500 border-yellow-300 dark:border-yellow-600' : ''}*/}
                        {/* Team Header */}
                        <div className="p-4">
                          {/* Top Section - Team Info */}
                          <div className="flex items-start justify-between mb-6">
                            <div className="team-card-content flex items-center gap-4 w-full">
                              {/* Team Icon with Status Indicator */}
                              <div className="relative flex-shrink-0">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300 shadow-sm border-2 
                                ${!team.is_deactivated ? 'bg-blue-200 dark:bg-blue-900/30' : 'bg-gray-200 dark:bg-gray-900/30'} 
                                ${!team.is_deactivated ? 'group-hover:bg-blue-300 dark:group-hover:bg-blue-800/40' : ''}
                                ${!team.is_deactivated ? 'border-green-400 dark:border-green-500' : 'border-red-400 dark:border-red-500'}
                                ${hasChanges ? 'inset-ring ring-yellow-400 dark:ring-yellow-500 border-yellow-300 dark:border-yellow-600' : ''}
                              `}>
                                  <HiOutlineUserGroup className={`w-7 h-7 ${!team.is_deactivated ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                                    }`} />
                                </div>

                                {/* Sync Changes Indicator */}
                                {hasChanges ? (
                                  <div
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-yellow-500 border-2 border-white dark:border-gray-800 flex items-center justify-center shadow-lg"
                                    title={`${teamSyncDiff.members.length} people change${teamSyncDiff.members.length > 1 ? 's' : ''} detected`}
                                  >
                                    <HiOutlineExclamationCircle className="w-3 h-3 text-white" />
                                  </div>
                                ) : (
                                  <div
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 
                                    ${!team.is_deactivated ? 'bg-green-500' : 'bg-red-500'}
                                  `}
                                    title={!team.is_deactivated ? 'Active Team' : 'Inactive Team'}
                                  />
                                )}
                              </div>

                              <div className="flex-1 min-w-0 max-w-[calc(100%-120px)] pr-3">
                                <TruncatedText
                                  text={team.name || 'Untitled Team'}
                                  className="font-bold text-sm text-gray-900 dark:text-white leading-tight mb-2
                                  transition-colors overflow-hidden text-ellipsis whitespace-nowrap max-w-full"
                                  as="h2"
                                  placement="top-start"
                                />

                                {/* Projects displayed where status badge was */}
                                {team.projects && team.projects.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {team.projects.slice(0, 2).map((project, index) => (
                                      <TruncatedText
                                        key={project.project_id}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap"
                                        text={project.project_name}
                                        placement="top-start"
                                      />
                                    ))}
                                    {team.projects.length > 2 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                        +{team.projects.length - 2}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs -mt-2 text-gray-500 dark:text-gray-400"> </span>
                                )}
                              </div>
                            </div>

                            <div className="team-actions flex items-center gap-2">
                              {/* Team Actions Dropdown */}
                              <div className="relative team-dropdown">
                                <button
                                  onClick={(e) => handleDropdownToggle(team.id, e)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                                 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                  <HiOutlineDotsVertical className="w-4 h-4" />
                                </button>

                                {uiState.dropdownOpenTeam === team.id && (
                                  <div className="absolute right-0 top-full mt-2 w-fit bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
                                    <button
                                      onClick={(e) => handleToggleActive(team, e)}
                                      className={`w-full pl-2 pr-4 py-2 text-left transition-all duration-200 flex items-center gap-2 rounded-lg text-sm ${!team.is_deactivated
                                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                                        }`}
                                    >
                                      {!team.is_deactivated ? <HiX className="w-4 h-4" /> : <HiCheck className="w-4 h-4" />}
                                      <span>{!team.is_deactivated ? 'Deactivate' : 'Activate'}</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Team Members Section */}
                          <div className="team-card-content space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                Team Members <span className="text-gray-500 dark:text-gray-400 font-normal">({team.members.length})</span>
                              </h3>
                            </div>

                            <div className="flex items-center">
                              <div className="flex -space-x-3">
                                {team.members.slice(0, 5).map((member: TeamMember, index: number) => (
                                  <div
                                    key={member.id}
                                    className="relative group/avatar"
                                    style={{ zIndex: 10 - index }}
                                  >
                                    <CustomTooltip title={member.name || 'No Name'} placement="top">
                                      <div
                                        className={`w-10 h-10 rounded-full border-3 border-white dark:border-gray-800 
                                         bg-gradient-to-br from-slate-300 to-slate-400 dark:from-slate-500 dark:to-slate-600 
                                         flex items-center justify-center text-white text-sm font-semibold
                                         ${!team.is_deactivated ? 'hover:border-blue-300 dark:hover:border-blue-600 hover:scale-110 hover:z-20' : ''}
                                         transition-all shadow-md cursor-default`}
                                      >
                                        {getInitials(member.name || '')}
                                      </div>
                                    </CustomTooltip>
                                  </div>
                                ))}
                                {team.members.length > 5 && (
                                  <CustomTooltip
                                    title={`Additional people: ${team.members.slice(5).map(m => m.name).join(', ')}`}
                                    placement="top"
                                  >
                                    <div className="w-10 h-10 rounded-full border-3 border-white dark:border-gray-800
                                        bg-gray-100 dark:bg-gray-700 flex items-center justify-center shadow-md hover:scale-110 ">
                                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                        +{team.members.length - 5}
                                      </span>
                                    </div>
                                  </CustomTooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {team.imported ? (
                        <div className="absolute bottom-4 right-4 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-blue-400 rounded-md text-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-400 shadow-sm z-10">
                          <HiDownload className="w-3 h-3" />
                          <span>Imported</span>
                        </div>
                      ) : (
                        <div className="absolute bottom-4 right-4 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-emerald-400 rounded-md text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-500 dark:text-emerald-400 shadow-sm z-10">
                          <HiHome className="w-3 h-3" />
                          <span>Cadence</span>
                        </div>
                      )}
                    </div>
                  </CustomTooltip>
                );
              })}
          </div>

          {/* No search results */}
          {searchTerm && filteredTeams.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-2xl mb-4">
                <HiOutlineUserGroup className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Teams Match Your Search
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Try adjusting your search terms or explore all teams by clearing the search.
              </p>
            </div>
          )}

          {/* No teams after filtering */}
          {!searchTerm && filteredTeams.length === 0 && teams.length > 0 && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-2xl mb-4">
                <HiOutlineUserGroup className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {showOnlyTeamsWithChanges ? 'No Teams with Changes' : 'No Active Teams'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                {showOnlyTeamsWithChanges
                  ? 'There are no teams with changes to display. Click "Show All Teams" to view all teams.'
                  : 'There are no active teams to display. Disable the "Show active only" toggle to view all teams.'
                }
              </p>
            </div>
          )}
        </div>
      </div >

      {/* Delete Confirmation Modal */}
      {
        uiState.showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mr-3">
                  <HiOutlineTrash className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Team
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete the team <span className="font-semibold">"{uiState.teamToDelete?.name}"</span>?
                This action cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700
                       rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete Team
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
});

export default TeamsContent; 